import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { enrollmentApi } from '../../../lib/api/enrollment.api';
import { errorText } from '../../../lib/error-text';
import { useTenantStore } from '../../../stores/tenant.store';
import { usePortalViewing } from '../../../stores/portal-view.store';

// ─── El alumno se da de baja solo (H8) ──────────────────────────────────────
// Solo ANTES del inicio de la sección: la inscripción se borra y no queda en el
// expediente. Desde el inicio, el retiro (W) lo registra el personal, así que
// este botón se muestra solo en un curso «Próximamente». El servidor lo exige
// igual (`enrollment.withdraw_after_start`). En «Ver portal» no aparece.

export function SelfDropButton({
  enrollmentId,
  courseName,
}: {
  enrollmentId: string | undefined;
  courseName: string;
}): JSX.Element | null {
  const { t } = useTranslation('sis');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentTenantId } = useTenantStore();
  const viewing = usePortalViewing();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (viewing || !enrollmentId || !currentTenantId) return null;

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await enrollmentApi.dropEnrollment({ tenantId: currentTenantId, enrollmentId });
      await queryClient.invalidateQueries({ queryKey: ['portal-v2-sections'] });
      setOpen(false);
      navigate('/portal-v2/courses');
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button size="small" color="inherit" onClick={() => setOpen(true)}>
        {t('portal.selfDrop.action')}
      </Button>
      <Dialog open={open} onClose={() => !busy && setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('portal.selfDrop.title')}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <DialogContentText>{t('portal.selfDrop.body', { course: courseName })}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={busy}>
            {t('portal.selfDrop.cancel')}
          </Button>
          <Button color="error" variant="contained" onClick={confirm} disabled={busy}>
            {busy ? t('portal.selfDrop.busy') : t('portal.selfDrop.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
